import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { Card, CardHeader, StatTile } from '../../../components/ui/Card';
import { InfoTip } from '../../../components/ui/InfoTip';
import type { useSdrActivity } from '../../../hooks/useProjectManagement';
import { formatDayMonthTime } from '../../../utils/dateFormat';
import { SdrSyncProblem, weekLabel } from './OverviewTab';
import type { SdrActivity, SdrActivityWeek } from '../../../api/projectManagement';
import {
  BAR_RADIUS_VERTICAL,
  CHART_AXIS,
  CHART_GRID,
  CHART_HEIGHT,
  CHART_LEGEND,
  CHART_SERIES,
  CHART_TOOLTIP,
} from '../../../components/ui/chart';

type SdrActivityQuery = ReturnType<typeof useSdrActivity>;

/**
 * Outbound → the SDR dialer in one tile, for the last full week (the headline
 * week of the Outbound page): "63 / 6", with this week so far in the sub-line.
 */
export function SdrDialsTile({ query }: { query: SdrActivityQuery }) {
  const data = query.data;
  if (query.error) return <StatTile label="SDR dials / conversations" value="—" sub="Apollo calls didn’t load" />;
  if (!data) return <StatTile label="SDR dials / conversations" value="…" sub="Loading Apollo calls" />;
  const { thisWeek, lastWeek, conversationMinSeconds } = data;
  return (
    <StatTile
      label="SDR dials / conversations"
      value={`${lastWeek.dials} / ${lastWeek.conversations}`}
      sub={`Last full week · Apollo · this week so far ${thisWeek.dials} / ${thisWeek.conversations}`}
      tooltip={`Dials / conversations in the last full week, from the Apollo dialer. A conversation is an outbound call of ${conversationMinSeconds}s or more.`}
    />
  );
}

/** Outbound → SDR dials per week, from the Apollo dialer. */
export function SdrDialsCard({ data }: { data: SdrActivity }) {
  const totalDials = data.weeks.reduce((n, w) => n + w.dials, 0);
  const totalConversations = data.weeks.reduce((n, w) => n + w.conversations, 0);

  return (
    <div className="space-y-3">
      <SdrSyncProblem sync={data} source="Apollo" missing="Calls" />
      <Card padding="md">
        <CardHeader
          title="SDR dials per week"
          description={`${totalDials} dials · ${totalConversations} conversations${data.lastSyncedAt ? ` · Apollo synced ${formatDayMonthTime(data.lastSyncedAt)}` : ''}`}
          action={
            <InfoTip>
              Calls placed from the SDR Apollo dialer. A dial is an outbound call; a conversation is an outbound call of{' '}
              {data.conversationMinSeconds} seconds or more. Weeks start on Monday, Brussels time.
            </InfoTip>
          }
        />
        <WeeklyCallsChart data={data.weeks} />
      </Card>
    </div>
  );
}

function WeeklyCallsChart({ data }: { data: SdrActivityWeek[] }) {
  const chartData = data.map((w) => ({
    week: weekLabel(w.weekStart),
    dials: w.dials,
    conversations: w.conversations,
  }));

  return (
    <div style={{ width: '100%', height: CHART_HEIGHT.default }}>
      <ResponsiveContainer>
        <BarChart data={chartData} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
          <CartesianGrid {...CHART_GRID} />
          <XAxis dataKey="week" {...CHART_AXIS} interval="preserveStartEnd" minTickGap={8} />
          <YAxis {...CHART_AXIS} allowDecimals={false} />
          <Tooltip {...CHART_TOOLTIP} labelFormatter={(label) => `Week of ${label}`} />
          <Legend {...CHART_LEGEND} formatter={(value) => <span className="text-text-secondary">{value}</span>} />
          <Bar dataKey="dials" name="Dials" fill={CHART_SERIES[0]} maxBarSize={20} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false} />
          <Bar dataKey="conversations" name="Conversations" fill={CHART_SERIES[1]} maxBarSize={20} radius={[...BAR_RADIUS_VERTICAL]} isAnimationActive={false} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
